import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import defaults from "./site.json";
import seed from "./seed.json";
const Context = createContext(null);
const HIDDEN = ["/services", "/for", "/insights", "/#method"];
const initial = {
  site: defaults,
  articles: seed.articles,
  categories: seed.categories,
  articleCategories: seed.articleCategories,
  pages: [],
};
export function ContentProvider({ children }) {
  const location = useLocation();
  const [content, setContent] = useState(initial),
    [state, setState] = useState({ loading: true, error: null });
  const params = new URLSearchParams(location.search);
  const preview = params.get("preview");
  const audience = params.get("audience");
  const path =
    location.pathname === "/contact" && audience
      ? "/for/" + audience
      : location.pathname;
  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, error: null });
    const query = new URLSearchParams({ path });
    if (preview) query.set("preview", preview);
    fetch("/api/content?" + query, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then(async (response) => {
        const result = await response.json();
        if (!response.ok)
          throw new Error(result.error || "We could not load this page.");
        if (!result.site) throw new Error("We could not load this page.");
        setContent(result);
        setState({ loading: false, error: null });
      })
      .catch((error) => {
        if (error.name !== "AbortError")
          setState({
            loading: false,
            error: preview
              ? error.message
              : "We could not load this page. Please try again.",
          });
      });
    return () => controller.abort();
  }, [path, preview]);
  const value = useMemo(() => {
    const cats = content.articleCategories || [];
    // The one-page experience went live on 2026-10-07; the older layout is kept only as reference.
    const nextLayout = true;
    return {
      ...content.site,
      ...content,
      // One page, one first step: the new layout links only to the check, About and Contact.
      NAV: nextLayout
        ? content.site.NAV.filter((n) => !HIDDEN.includes(n.to))
        : content.site.NAV,
      nextLayout,
      loading: state.loading,
      error: state.error,
      AUDIENCES: content.categories,
      PAPERS: content.articles.map((a) => ({
        ...a,
        id: a.slug,
        text: a.excerpt,
        img: a.image,
        read:
          a.read ||
          Math.max(
            1,
            Math.ceil(
              a.body
                .map((b) => b.text || (b.items || []).join(" "))
                .join(" ")
                .split(/\s+/).length / 200,
            ),
          ),
        cat:
          cats.find((c) => c.slug === a.categories[0])?.title ||
          a.categories[0] ||
          "Notes",
      })),
      PAPER_POINTS: {},
      ARTICLE_CATEGORIES: cats,
      text: (key, values = {}) =>
        String(content.site.COPY[key] ?? defaults.COPY[key] ?? key).replace(
          /\{(\w+)\}/g,
          (match, name) => values[name] ?? match,
        ),
    };
  }, [content, state]);
  return (
    <Context.Provider value={value}>
      {content.preview && (
        <div
          className="fixed bottom-4 left-4 z-[70] rounded-full bg-black px-5 py-3 text-[12px] text-white shadow-lg"
          role="status"
        >
          Draft preview · revision {content.preview.revision} · not live
        </div>
      )}
      {state.error ? (
        <main className="mx-auto min-h-screen max-w-[1400px] px-5 pt-40">
          <h1 className="nm-title text-[36px]">{state.error}</h1>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 rounded-full bg-black px-6 py-3 text-white"
          >
            Try again
          </button>
        </main>
      ) : (
        children
      )}
    </Context.Provider>
  );
}
export function useContent() {
  const context = useContext(Context);
  if (!context) throw new Error("ContentProvider is missing.");
  return context;
}
export function ContentLoading() {
  return (
    <section className="min-h-[60vh] px-5 pt-40" role="status">
      <div className="mx-auto max-w-[1400px] animate-pulse text-[18px] opacity-50">
        Loading…
      </div>
    </section>
  );
}
